// UserContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
import api from './api';

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [lastSyncTime, setLastSyncTime] = useState(null);

    // Sync user data with backend
    const syncUserWithBackend = useCallback(async () => {
        const storedUser = localStorage.getItem('sat_user');
        if (!storedUser) return;

        try {
            const parsedUser = JSON.parse(storedUser);
            if (!parsedUser.user_id) return;

            // Fetch latest user data from backend
            const response = await api.get(`/api/user/get_user_profile/`);
            const backendUser = response.data;

            // Check if premium status changed
            if (backendUser.is_premium !== parsedUser.is_premium) {
                // Update user with latest backend data
                setUser(backendUser);
            } else {
                // Update with latest data even if premium status same
                setUser(backendUser);
            }

            setLastSyncTime(Date.now());
        } catch (error) {
            console.error('Failed to sync user with backend:', error);
        }
    }, []);

    // Restore user from localStorage if available
    useEffect(() => {
        const storedUser = localStorage.getItem('sat_user');
        if (storedUser) {
            setUser(JSON.parse(storedUser));
            // Sync with backend immediately on mount
            syncUserWithBackend();
        }
    }, [syncUserWithBackend]);

    // Periodic sync every 5 minutes
    useEffect(() => {
        if (!user) return;

        const SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes
        const interval = setInterval(() => {
            syncUserWithBackend();
        }, SYNC_INTERVAL);

        return () => clearInterval(interval);
    }, [user, syncUserWithBackend]);

    // Update localStorage whenever user changes
    useEffect(() => {
        if (user) {
            localStorage.removeItem('sat_user');
            localStorage.setItem('sat_user', JSON.stringify(user));
        } else {
            localStorage.removeItem('sat_user');
        }
    }, [user]);

    return (
        <UserContext.Provider value={{ user, setUser, syncUserWithBackend, lastSyncTime }}>
            {children}
        </UserContext.Provider>
    );
};
